import React, { useState, useEffect, useCallback } from "react";
import ClientBackground from "./ClientBackground";
import { ChevronLeft, ChevronRight, X, Calendar, Clock, Plus, Trash2, Edit2, Check } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../api";
import LoadingSpinner from "./LoadingSpinner";

export default function AddReminder() {
  const navigate = useNavigate();
  const { childId, reminderId } = useParams();
  
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDates, setSelectedDates] = useState([]);
  const [selectedTime, setSelectedTime] = useState({ hour: "11", minute: "38", period: "AM" });
  const [existingReminders, setExistingReminders] = useState([]);
  const [reminderTitle, setReminderTitle] = useState("");
  const [loading, setLoading] = useState(false);
  const [showDateModal, setShowDateModal] = useState(false);
  const [clickedDate, setClickedDate] = useState(null);
  const [showRemindersModal, setShowRemindersModal] = useState(false);
  const [dateReminders, setDateReminders] = useState([]);
  const [editingReminder, setEditingReminder] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  const daysOfWeek = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const months = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

  const fetchReminderToEdit = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/children/reminders/${reminderId}/`);
      const reminder = response.data;
      setReminderTitle(reminder.title);
      setSelectedDates([reminder.reminder_date]);
      const [time, period] = reminder.formatted_time.split(' ');
      const [hour, minute] = time.split(':');
      setSelectedTime({ hour, minute, period });
    } catch (error) {
      console.error("Error fetching reminder to edit:", error);
    } finally {
      setLoading(false);
    }
  }, [reminderId]);

  const fetchReminders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/children/reminders/by-child/?child_id=${childId}`);
      setExistingReminders(response.data || []);
    } catch (error) {
      console.error("Error fetching reminders:", error);
      setExistingReminders([]);
    } finally {
      setLoading(false);
    }
  }, [childId]);

  useEffect(() => {
    if (reminderId) {
      fetchReminderToEdit();
    }
    fetchReminders();
  }, [childId, reminderId, fetchReminderToEdit, fetchReminders]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    
    const days = [];
    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }
    return days;
  };

  const changeMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const handleDateClick = async (day) => {
    if (!day) return;
    
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setClickedDate(dateStr);
    
    const remindersForDate = existingReminders.filter(r => r.reminder_date === dateStr);
    setDateReminders(remindersForDate);
    setShowDateModal(true);
  };

  const handleAddToReminder = () => {
    if (clickedDate && !selectedDates.includes(clickedDate)) {
      setSelectedDates([...selectedDates, clickedDate]);
    }
    setShowDateModal(false);
  };

  const handleShowReminders = () => {
    setShowDateModal(false);
    setShowRemindersModal(true);
  };

  const removeSelectedDate = (dateStr) => {
    setSelectedDates(selectedDates.filter(d => d !== dateStr));
  };

  const isDateSelected = (day) => {
    if (!day) return false;
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return selectedDates.includes(dateStr);
  };

  const hasReminders = (day) => {
    if (!day) return false;
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return existingReminders.some(r => r.reminder_date === dateStr);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const dayNum = date.getDate();
    const options = { weekday: 'long' };
    const weekday = date.toLocaleDateString('en-US', options);
    return `${weekday}, ${dayNum}${getOrdinal(dayNum)} ${months[date.getMonth()]}, ${date.getFullYear()}`;
  };

  const getOrdinal = (day) => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
      case 1: return "st";
      case 2: return "nd";
      case 3: return "rd";
      default: return "th";
    }
  };

  const handleSave = async () => {
    if (selectedDates.length === 0 || !reminderTitle.trim()) {
      alert("Please select at least one date and enter a reminder title");
      return;
    }

    setLoading(true);
    const timeString = `${selectedTime.hour}:${selectedTime.minute} ${selectedTime.period}`;

    try {
      if (reminderId) {
        // Update existing reminder
        await api.patch(`/children/reminders/${reminderId}/`, {
          title: reminderTitle,
          reminder_date: selectedDates[0],
          reminder_time: timeString,
        });
        setShowSuccessMessage(true);
        setTimeout(() => {
          setShowSuccessMessage(false);
          navigate(`/reminder-details/${childId}`);
        }, 2000);
      } else {
        // Create new reminders
        const response = await api.post('/children/reminders/bulk-create/', {
          child_id: childId,
          title: reminderTitle,
          dates: selectedDates,
          time: timeString,
          description: '',
          color: 'cyan'
        });
        setExistingReminders([...existingReminders, ...response.data]);
        setSelectedDates([]);
        setReminderTitle('');
        setShowSuccessMessage(true);
        setTimeout(() => {
          setShowSuccessMessage(false);
        }, 3000);
      }
    } catch (error) {
      console.error("Error saving reminder(s):", error);
      alert("Failed to save reminder(s). Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteReminder = async (reminderId) => {
    if (!window.confirm("Are you sure you want to delete this reminder?")) {
      return;
    }

    try {
      await api.delete(`/children/reminders/${reminderId}/`);
      
      setExistingReminders(existingReminders.filter(r => r.id !== reminderId));
      setDateReminders(dateReminders.filter(r => r.id !== reminderId));
      
      if (dateReminders.filter(r => r.id !== reminderId).length === 0) {
        setShowRemindersModal(false);
      }
    } catch (error) {
      console.error("Error deleting reminder:", error);
      alert("Failed to delete reminder. Please try again.");
    }
  };

  const handleEditReminder = (reminder) => {
    setEditingReminder({
      id: reminder.id,
      title: reminder.title,
      time: reminder.formatted_time
    });
  };

  const handleUpdateReminder = async (reminderId) => {
    if (!editingReminder.title.trim()) {
      alert("Please enter a reminder title");
      return;
    }

    try {
      await api.patch(`/children/reminders/${reminderId}/`, {
        title: editingReminder.title
      });
      
      const updatedReminders = existingReminders.map(r => 
        r.id === reminderId ? { ...r, title: editingReminder.title } : r
      );
      setExistingReminders(updatedReminders);
      
      const updatedDateReminders = dateReminders.map(r => 
        r.id === reminderId ? { ...r, title: editingReminder.title } : r
      );
      setDateReminders(updatedDateReminders);
      
      setEditingReminder(null);
    } catch (error) {
      console.error("Error updating reminder:", error);
      alert("Failed to update reminder. Please try again.");
    }
  };

  const days = getDaysInMonth(currentDate);

  if (loading && existingReminders.length === 0) {
    return (
      <div className="relative min-h-screen">
        <ClientBackground />
        <div className="relative z-10 flex items-center justify-center min-h-screen">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      <ClientBackground />
      <div className="relative z-10 min-h-screen pb-8">
        <div className="bg-white/30 backdrop-blur-sm px-5 py-4 flex items-center justify-between sticky top-0 z-20">
          <button onClick={() => navigate(-1)} className="p-2">
            <ChevronLeft className="w-6 h-6 text-gray-800" />
          </button>
          <h1 className="text-lg md:text-xl font-semibold text-[#1e3a5f]">Add Reminder</h1>
          <div className="w-10" />
        </div>

        {showSuccessMessage && (
          <div className="fixed top-20 left-1/2 transform -translate-x-1/2 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center gap-2">
            <Check className="w-5 h-5" />
            <span>Reminders saved successfully!</span>
          </div>
        )}

        <div className="px-5 md:px-8 lg:px-12 pt-6 max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-6">
            <div>
              <div className="bg-white rounded-3xl shadow-lg p-6 md:p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-base md:text-lg font-semibold text-[#1e3a5f]">
                    {months[currentDate.getMonth()]} {currentDate.getFullYear()}
                  </h2>
                  <div className="flex gap-4">
                    <button onClick={() => changeMonth(-1)} className="p-1 hover:bg-gray-100 rounded">
                      <ChevronLeft className="w-5 h-5 text-gray-600" />
                    </button>
                    <button onClick={() => changeMonth(1)} className="p-1 hover:bg-gray-100 rounded">
                      <ChevronRight className="w-5 h-5 text-gray-600" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-7 gap-2">
                  {daysOfWeek.map(day => (
                    <div key={day} className="text-center text-xs font-medium text-gray-500 pb-2">
                      {day}
                    </div>
                  ))}
                  {days.map((day, index) => (
                    <button
                      key={index}
                      onClick={() => handleDateClick(day)}
                      disabled={!day}
                      className={`relative aspect-square flex items-center justify-center rounded-lg text-sm font-medium transition ${
                        day ? 'hover:bg-gray-100' : ''
                      } ${
                        isDateSelected(day) ? 'bg-cyan-400 text-white' : day ? 'text-gray-800' : ''
                      }`}
                    >
                      {day}
                      {hasReminders(day) && (
                        <div className="absolute bottom-1 w-1 h-1 bg-red-500 rounded-full"></div>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-4 text-xs text-gray-600">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-cyan-400 rounded"></div>
                  <span>Selected for new reminder</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-white border border-gray-300 rounded flex items-center justify-center">
                    <div className="w-1 h-1 bg-red-500 rounded-full"></div>
                  </div>
                  <span>Has existing reminders</span>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-3xl shadow-lg p-6 md:p-8">
                <h3 className="text-base md:text-lg font-semibold text-[#1e3a5f] mb-4">Reminder Title</h3>
                <input
                  type="text"
                  value={reminderTitle}
                  onChange={(e) => setReminderTitle(e.target.value)}
                  placeholder="e.g., Doctor's Checkup"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-cyan-300 text-[#1e3a5f]"
                />
              </div>

              <div className="bg-white rounded-3xl shadow-lg p-6 md:p-8">
                <h3 className="text-base md:text-lg font-semibold text-[#1e3a5f] mb-4">
                  <Clock className="inline w-5 h-5 mr-2" />
                  Reminder Time
                </h3>
                <div className="flex items-center gap-3 justify-center">
                  <input
                    type="text"
                    value={selectedTime.hour}
                    onChange={(e) => setSelectedTime({ ...selectedTime, hour: e.target.value })}
                    className="w-16 text-center text-2xl font-semibold bg-cyan-100 rounded-lg py-2 text-[#1e3a5f] focus:outline-none focus:ring-2 focus:ring-cyan-300"
                    maxLength={2}
                  />
                  <span className="text-2xl font-semibold text-[#1e3a5f]">:</span>
                  <input
                    type="text"
                    value={selectedTime.minute}
                    onChange={(e) => setSelectedTime({ ...selectedTime, minute: e.target.value })}
                    className="w-16 text-center text-2xl font-semibold bg-cyan-100 rounded-lg py-2 text-[#1e3a5f] focus:outline-none focus:ring-2 focus:ring-cyan-300"
                    maxLength={2}
                  />
                  <div className="flex gap-2 ml-2">
                    <button
                      onClick={() => setSelectedTime({ ...selectedTime, period: 'AM' })}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                        selectedTime.period === 'AM' ? 'bg-cyan-400 text-white' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      AM
                    </button>
                    <button
                      onClick={() => setSelectedTime({ ...selectedTime, period: 'PM' })}
                      className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                        selectedTime.period === 'PM' ? 'bg-cyan-400 text-white' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      PM
                    </button>
                  </div>
                </div>
              </div>

              {selectedDates.length > 0 && (
                <div className="bg-white rounded-3xl shadow-lg p-6 md:p-8">
                  <h3 className="text-base md:text-lg font-semibold text-[#1e3a5f] mb-4">
                    <Calendar className="inline w-5 h-5 mr-2" />
                    Selected Dates ({selectedDates.length})
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Reminder will buzz on these dates at {selectedTime.hour}:{selectedTime.minute} {selectedTime.period}
                  </p>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {selectedDates.sort().map((dateStr) => (
                      <div key={dateStr} className="bg-cyan-50 rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-[#1e3a5f]">
                            {formatDate(dateStr)}
                          </p>
                          {reminderTitle && (
                            <p className="text-xs text-gray-600 mt-1">{reminderTitle}</p>
                          )}
                        </div>
                        <button
                          onClick={() => removeSelectedDate(dateStr)}
                          className="p-1 hover:bg-red-100 rounded-full transition"
                        >
                          <X className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-4">
                <button
                  onClick={handleSave}
                  disabled={loading || selectedDates.length === 0 || !reminderTitle.trim()}
                  className="w-full bg-[#1e3a5f] hover:bg-[#2d4a6f] text-white py-4 rounded-2xl font-semibold text-base transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <span>Saving...</span>
                  ) : (
                    <>
                      <Plus className="w-5 h-5" />
                      <span>{reminderId ? "Update Reminder" : "Save Reminder"}</span>
                    </>
                  )}
                </button>
                {reminderId && (
                  <button
                    onClick={() => handleDeleteReminder(reminderId)}
                    disabled={loading}
                    className="w-full bg-red-500 hover:bg-red-600 text-white py-4 rounded-2xl font-semibold text-base transition disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Trash2 className="w-5 h-5" />
                    <span>Delete</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {showDateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl p-6 max-w-sm w-full">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#1e3a5f]">
                  {formatDate(clickedDate)}
                </h3>
                <button onClick={() => setShowDateModal(false)} className="p-1">
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <div className="space-y-3">
                <button
                  onClick={handleAddToReminder}
                  className="w-full bg-cyan-400 hover:bg-cyan-500 text-white py-3 rounded-xl font-medium transition flex items-center justify-center gap-2"
                >
                  <Plus className="w-5 h-5" />
                  Add to Reminder
                </button>
                {dateReminders.length > 0 && (
                  <button
                    onClick={handleShowReminders}
                    className="w-full bg-[#1e3a5f] hover:bg-[#2d4a6f] text-white py-3 rounded-xl font-medium transition flex items-center justify-center gap-2"
                  >
                    <Calendar className="w-5 h-5" />
                    Show Reminders ({dateReminders.length})
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {showRemindersModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl p-6 max-w-md w-full max-h-[80vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-[#1e3a5f]">
                  Reminders for {formatDate(clickedDate)}
                </h3>
                <button onClick={() => setShowRemindersModal(false)} className="p-1">
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <div className="space-y-3">
                {dateReminders.map((reminder) => (
                  <div key={reminder.id} className="bg-pink-50 rounded-lg p-4">
                    {editingReminder?.id === reminder.id ? (
                      <div className="space-y-3">
                        <input
                          type="text"
                          value={editingReminder.title}
                          onChange={(e) => setEditingReminder({ ...editingReminder, title: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-cyan-300 text-[#1e3a5f]"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleUpdateReminder(reminder.id)}
                            className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2 rounded-lg font-medium transition flex items-center justify-center gap-1"
                          >
                            <Check className="w-4 h-4" />
                            Save
                          </button>
                          <button
                            onClick={() => setEditingReminder(null)}
                            className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 rounded-lg font-medium transition"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex-1">
                            <p className="font-semibold text-[#1e3a5f] mb-1">{reminder.title}</p>
                            <p className="text-sm text-gray-600">
                              <Clock className="inline w-4 h-4 mr-1" />
                              {reminder.formatted_time}
                            </p>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditReminder(reminder)}
                              className="p-2 hover:bg-blue-100 rounded-lg transition"
                            >
                              <Edit2 className="w-4 h-4 text-blue-600" />
                            </button>
                            <button
                              onClick={() => handleDeleteReminder(reminder.id)}
                              className="p-2 hover:bg-red-100 rounded-lg transition"
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
